import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ClipboardCheck,
  Eye,
  EyeOff,
  Mail,
  Moon,
  Sun,
  X,
  Zap,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordStrengthMeter from '../component/common/PasswordStrengthMeter';
import logoMark from '../img/logo-branco.png';
import InputMasked from '../component/input/InputMasked';
import { standaloneFieldSx } from '../common/fieldStyles';
import { useColorMode } from '../hooks/useColorMode';
import { LEGAL_LINKS_ENABLED } from './legal/legalLinks';
import MobileLogin from '../component/auth/MobileLogin';
import { useIsMobileShell } from '../hooks/useIsMobileShell';
import { useEnterpriseConfigContext } from '../component/context/EnterpriseConfigContext';

const LoginSchema = Yup.object().shape({
  username: Yup.string()
    .required('Username é obrigatório'),
  password: Yup.string()
    .required('Senha é obrigatória'),
  codeClient: Yup.string()
    .required('Código do cliente é obrigatório')
});

const RegisterSchema = Yup.object().shape({
  codEnterprise: Yup.string()
    .required('Nome do estabelecimento é obrigatório'),
  segment: Yup.string()
    .required('Segmento é obrigatório'),
  name: Yup.string()
    .required('Nome é obrigatório'),
  lastName: Yup.string()
    .required('Sobrenome é obrigatório'),
  email: Yup.string()
    .email('E-mail inválido')
    .required('E-mail é obrigatório'),
  cellphone: Yup.string()
    .required('Celular é obrigatório'),
  password: Yup.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .required('Senha é obrigatória'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Senhas devem ser iguais')
    .required('Confirmação de senha é obrigatória')
});

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('E-mail inválido')
    .required('E-mail é obrigatório')
});

const FEATURE_PILLS = [
  { icon: Zap, label: 'Frente de caixa rápida' },
  { icon: ClipboardCheck, label: 'Checklist digital' },
  { icon: BarChart3, label: 'Relatórios completos' },
];

/**
 * `ff` floating-label text field — DESIGN-SYSTEM.md §5.5 "Forms" flavor A,
 * reused here for the auth forms. Aplica a casca `standaloneFieldSx` (52px,
 * radius 12px, label interno sem notch) e mescla o `sx` recebido (margens).
 */
function AuthField({ sx, ...props }) {
  return (
    <TextField
      variant="outlined"
      fullWidth
      sx={[standaloneFieldSx, ...(Array.isArray(sx) ? sx : [sx])]}
      {...props}
    />
  );
}

// Override local do modo cadastro (ui-ux/prototipo-criar-conta.html): campos
// de 46px em vez dos 52px do `standaloneFieldSx` global. Aplicar DEPOIS dele.
const compactFieldSx = {
  '& .MuiInputLabel-root': { transform: 'translate(14px, 14px) scale(1)' },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': { transform: 'translate(14px, 6px) scale(1)' },
  '& .MuiOutlinedInput-root': { height: '46px' },
  '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': { padding: '19px 14px 5px 14px' },
  '& .MuiSelect-select.MuiOutlinedInput-input': { padding: '19px 32px 5px 14px', display: 'block' },
};

// Grid de 2 colunas (1 coluna abaixo de 640px) de cada seção do cadastro.
const compactGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  columnGap: '14px',
  alignItems: 'start',
};

/** Rótulo de seção do cadastro compacto (padrão local desta tela). */
function SectionLabel({ children, first }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '10.5px', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'text.disabled', mt: first ? '2px' : 0, mb: '8px', '&::after': { content: '""', flex: 1, height: '1px', backgroundColor: 'divider' } }}>
      {children}
    </Box>
  );
}

export default function Login({ setIsAuthenticated }) {
  const [errorInvalidCredentials, setErrorInvalidCredentials] = useState(false);
  // `/login?modo=cadastro` (CTA "Criar minha conta" da landing) abre direto na aba de cadastro.
  const [searchParams] = useSearchParams();
  const [isLoginMode, setIsLoginMode] = useState(() => searchParams.get('modo') !== 'cadastro');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const navigate = useNavigate();
  const isMobileShell = useIsMobileShell();
  const theme = useTheme();
  const { mode, toggleMode: toggleColorMode } = useColorMode();
  const { refetch: refetchEnterpriseConfig } = useEnterpriseConfigContext();

  const decodeToken = (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  const handleLogin = async (codeClient, username, password) => {
    setIsLoading(true);
    setErrorInvalidCredentials(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code_client: codeClient,
          password: password,
          username: username
        })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        setIsAuthenticated(true);
        const tokenData = decodeToken(data.access_token);
        if (tokenData) {
          tokenData.modules.forEach(module => {
            localStorage.setItem(module, true);
          });
        }
        // O contexto de configurações da empresa é carregado uma única vez no
        // início do app; recarrega com o token recém-obtido para não manter as
        // configurações de outra empresa/sessão anterior (ex.: os_use_checklist).
        await refetchEnterpriseConfig();
        navigate('/home');
      } else {
        setErrorInvalidCredentials(true);
      }
    } catch (error) {
      console.error('Erro no login:', error);
      setErrorInvalidCredentials(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (values) => {
    setRegisterLoading(true);
    setRegisterError('');
    setRegisterSuccess(false);

    const data = {
      enterprise_legal_name: values.codEnterprise,
      enterprise_owner_name: `${values.name} ${values.lastName}`,
      enterprise_email: values.email,
      password: values.password,
      cellphone: values.cellphone,
      segment: values.segment
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setRegisterSuccess(true);
        setRegisterError('');
        setTimeout(() => {
          setIsLoginMode(true);
          setRegisterSuccess(false);
          registerFormik.resetForm();
          setTermsAccepted(false);
        }, 3000);
      } else {
        const errorData = await response.json();
        setRegisterError(errorData.message || 'Erro ao realizar cadastro');
      }
    } catch (error) {
      setRegisterError('Erro de conexão. Tente novamente.');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleForgotPassword = async (values) => {
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription/resetpassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          code_client: values.codClient
        })
      });

      if (response.ok) {
        setForgotPasswordSuccess(true);
        setForgotPasswordError('');
      } else {
        const errorData = await response.json();
        setForgotPasswordError(errorData.message || 'Erro ao enviar email de recuperação');
      }
    } catch (error) {
      setForgotPasswordError('Erro de conexão. Tente novamente.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const loginFormik = useFormik({
    initialValues: {
      codeClient: '',
      username: '',
      password: ''
    },
    validationSchema: LoginSchema,
    onSubmit: (values) => {
      handleLogin(values.codeClient, values.username, values.password);
    },
  });

  const registerFormik = useFormik({
    initialValues: {
      codEnterprise: '',
      segment: '',
      name: '',
      lastName: '',
      email: '',
      cellphone: '',
      password: '',
      confirmPassword: ''
    },
    validationSchema: RegisterSchema,
    onSubmit: (values) => {
      handleRegister(values);
    },
  });

  const forgotPasswordFormik = useFormik({
    initialValues: {
      codClient: '',
      email: ''
    },
    validationSchema: ForgotPasswordSchema,
    onSubmit: (values) => {
      handleForgotPassword(values);
    },
  });

  const toggleMode = (nextMode) => {
    const nextIsLogin = nextMode === 'login';
    if (nextIsLogin === isLoginMode) return;
    setIsLoginMode(nextIsLogin);
    setErrorInvalidCredentials(false);
    setRegisterSuccess(false);
    setRegisterError('');
    if (isLoginMode) {
      registerFormik.resetForm();
      setTermsAccepted(false);
    } else {
      loginFormik.resetForm();
    }
  };

  const closeForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);
    forgotPasswordFormik.resetForm();
  };

  const confirmPasswordMatches = registerFormik.values.confirmPassword.length > 0 && !registerFormik.errors.confirmPassword;

  const forgotDialog = (
    <>
      {/* Esqueceu sua senha — modal central, DESIGN-SYSTEM.md §5.6 */}
      <Dialog open={forgotPasswordOpen} onClose={closeForgotPassword} maxWidth="xs" fullWidth>
        <Box sx={{ position: 'relative', p: '26px 24px 6px', textAlign: 'center' }}>
          <IconButton onClick={closeForgotPassword} sx={{ position: 'absolute', top: 14, right: 14 }}>
            <X size={16} />
          </IconButton>
          {!forgotPasswordSuccess ? (
            <>
              <Box sx={{ width: 52, height: 52, borderRadius: '16px', backgroundImage: theme.palette.gradient.brand, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.75 }}>
                <Mail size={22} />
              </Box>
              <Typography sx={{ fontSize: 17, fontWeight: 800, mb: 0.75 }}>Esqueceu sua senha?</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                Informe seu código de cliente e e-mail cadastrado. Enviaremos um link para você criar uma nova senha.
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ width: 56, height: 56, borderRadius: '50%', backgroundColor: 'success.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2, mt: 1 }}>
                <CheckCircle2 size={26} />
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 0.75 }}>Link enviado!</Typography>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                Confira sua caixa de entrada em <b>{forgotPasswordFormik.values.email}</b> para redefinir sua senha.
              </Typography>
            </>
          )}
        </Box>

        {!forgotPasswordSuccess && (
          <Box component="form" onSubmit={forgotPasswordFormik.handleSubmit} sx={{ p: '18px 24px' }}>
            {forgotPasswordError && <Alert severity="error" sx={{ mb: 2 }}>{forgotPasswordError}</Alert>}
            <AuthField
              sx={{ mb: 2 }}
              name="codClient"
              label="Código do cliente"
              value={forgotPasswordFormik.values.codClient}
              onChange={(e) => forgotPasswordFormik.setFieldValue('codClient', e.target.value)}
              error={!!forgotPasswordFormik.errors.codClient}
              helperText={forgotPasswordFormik.errors.codClient}
            />
            <AuthField
              name="email"
              type="email"
              label="E-mail"
              value={forgotPasswordFormik.values.email}
              onChange={(e) => forgotPasswordFormik.setFieldValue('email', e.target.value)}
              error={!!forgotPasswordFormik.errors.email}
              helperText={forgotPasswordFormik.errors.email}
            />
          </Box>
        )}

        <Box sx={{ p: '6px 24px 24px', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {!forgotPasswordSuccess ? (
            <>
              <Button
                variant="contained"
                color="primary"
                disabled={forgotPasswordLoading}
                onClick={forgotPasswordFormik.handleSubmit}
                sx={{ height: 46 }}
              >
                {forgotPasswordLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Enviar link de redefinição'}
              </Button>
              <Button variant="outlined" onClick={closeForgotPassword} sx={{ height: 42 }}>Cancelar</Button>
            </>
          ) : (
            <Button variant="outlined" onClick={closeForgotPassword} sx={{ height: 42 }}>Fechar</Button>
          )}
        </Box>
      </Dialog>
    </>
  );

  if (isMobileShell) {
    return (
      <>
        <MobileLogin
          isLoginMode={isLoginMode}
          onToggleMode={(v) => toggleMode(v)}
          loginFormik={loginFormik}
          registerFormik={registerFormik}
          errorInvalidCredentials={errorInvalidCredentials}
          registerSuccess={registerSuccess}
          registerError={registerError}
          isLoading={isLoading}
          registerLoading={registerLoading}
          showLoginPassword={showLoginPassword}
          setShowLoginPassword={setShowLoginPassword}
          showRegisterPassword={showRegisterPassword}
          setShowRegisterPassword={setShowRegisterPassword}
          showConfirmPassword={showConfirmPassword}
          setShowConfirmPassword={setShowConfirmPassword}
          termsAccepted={termsAccepted}
          setTermsAccepted={setTermsAccepted}
          onForgotPassword={() => setForgotPasswordOpen(true)}
          mode={mode}
          onToggleTheme={toggleColorMode}
        />
        {forgotDialog}
      </>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: isLoginMode ? 6 : { xs: 3, sm: 2.5 },
        backgroundColor: 'background.default',
      }}
    >
      {/* aurora background — DESIGN-SYSTEM.md prototipo-login-cadastro.html */}
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <Box sx={{ position: 'absolute', width: 560, height: 560, borderRadius: '50%', filter: 'blur(90px)', opacity: mode === 'dark' ? 0.28 : 0.5, backgroundColor: 'primary.main', top: -160, left: -120 }} />
        <Box sx={{ position: 'absolute', width: 480, height: 480, borderRadius: '50%', filter: 'blur(90px)', opacity: mode === 'dark' ? 0.28 : 0.5, backgroundColor: 'brand2.main', bottom: -140, right: -100 }} />
        <Box sx={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', filter: 'blur(90px)', opacity: mode === 'dark' ? 0.22 : 0.32, backgroundColor: 'secondary.main', top: '38%', left: '48%' }} />
      </Box>

      <Box
        component="button"
        onClick={toggleColorMode}
        title="Alternar tema"
        sx={{
          position: 'fixed', top: 22, right: 22, zIndex: 10, width: 40, height: 40, borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`, backgroundColor: alpha(theme.palette.background.paper, 0.7),
          backdropFilter: 'blur(10px)', color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: theme.customShadows.sm,
          '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
        }}
      >
        {mode === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
      </Box>

      <Box sx={{ position: 'relative', zIndex: 2, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* brand lockup */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, mb: isLoginMode ? 3.5 : '12px' }}>
          <Box sx={{ width: 42, height: 42, borderRadius: '13px', backgroundImage: theme.palette.gradient.brand, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 26px rgba(37,99,235,.35)' }}>
            {/* Marca oficial (mesma composição do selo da sidebar/`.logo-badge`
                da landing: gradiente da marca + logo branco), no lugar do
                placeholder "FV" do protótipo. */}
            <Box component="img" src={logoMark} alt="FlowVendas" sx={{ width: 25, height: 25, display: 'block', objectFit: 'contain' }} />
          </Box>
          <Typography sx={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.01em' }}>
            Flow<Box component="span" sx={{ color: 'brand2.main' }}>Vendas</Box>
          </Typography>
        </Box>

        {/* headline */}
        <Box sx={{ textAlign: 'center', mb: isLoginMode ? 3.75 : '14px', maxWidth: isLoginMode ? 440 : 560 }}>
          <Typography component="h1" sx={{ fontSize: isLoginMode ? 27 : 24, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2, mb: isLoginMode ? 1 : '4px' }}>
            {isLoginMode ? 'Acesse sua conta' : 'Crie sua conta grátis'}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            {isLoginMode ? '' : 'Leva menos de 2 minutos para começar a vender.'}
          </Typography>
        </Box>

        {/* glass card */}
        <Box
          sx={{
            position: 'relative', width: '100%', maxWidth: isLoginMode ? 420 : 680,
            backgroundColor: alpha(theme.palette.background.paper, 0.65),
            backdropFilter: 'blur(24px) saturate(160%)',
            border: `1px solid ${alpha('#ffffff', mode === 'dark' ? 0.08 : 0.5)}`,
            borderRadius: '26px', boxShadow: theme.customShadows.lg,
            p: isLoginMode ? { xs: '28px 22px 26px', sm: '34px 32px 30px' } : { xs: '22px 18px 18px', sm: '22px 28px 18px' },
          }}
        >
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={isLoginMode ? 'login' : 'cadastro'}
            onChange={(e, v) => v && toggleMode(v)}
            sx={{ mb: isLoginMode ? 3.25 : '16px', ...(isLoginMode ? {} : { maxWidth: 300, mx: 'auto' }) }}
          >
            <ToggleButton value="login" sx={{ flex: 1, fontWeight: 700 }}>Entrar</ToggleButton>
            <ToggleButton value="cadastro" sx={{ flex: 1, fontWeight: 700 }}>Criar conta</ToggleButton>
          </ToggleButtonGroup>

          {errorInvalidCredentials && (
            <Alert severity="error" sx={{ width: '100%', mb: 2.5 }}>
              Credenciais inválidas. Verifique seus dados e tente novamente.
            </Alert>
          )}
          {registerSuccess && (
            <Alert severity="success" sx={{ width: '100%', mb: 2.5 }}>
              Cadastro realizado! Um e-mail foi enviado para ativação da sua conta.
            </Alert>
          )}
          {registerError && (
            <Alert severity="error" sx={{ width: '100%', mb: 2.5 }}>
              {registerError}
            </Alert>
          )}

          {isLoginMode ? (
            <Box component="form" onSubmit={loginFormik.handleSubmit} sx={{ width: '100%' }}>
              <AuthField
                sx={{ mb: 2 }}
                name="codeClient"
                label="Código do cliente"
                autoFocus
                value={loginFormik.values.codeClient}
                onChange={(e) => loginFormik.setFieldValue('codeClient', e.target.value)}
                error={!!loginFormik.errors.codeClient}
                helperText={loginFormik.errors.codeClient}
              />
              <AuthField
                sx={{ mb: 2 }}
                name="username"
                label="Usuário"
                autoComplete="username"
                value={loginFormik.values.username}
                onChange={(e) => loginFormik.setFieldValue('username', e.target.value)}
                error={!!loginFormik.errors.username}
                helperText={loginFormik.errors.username}
              />
              <AuthField
                sx={{ mb: 1 }}
                name="password"
                label="Senha"
                type={showLoginPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={loginFormik.values.password}
                onChange={(e) => loginFormik.setFieldValue('password', e.target.value)}
                error={!!loginFormik.errors.password}
                helperText={loginFormik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label="alternar visibilidade da senha" onClick={() => setShowLoginPassword(!showLoginPassword)} edge="end">
                        {showLoginPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, mt: 0.5 }}>
                <Button
                  type="button"
                  onClick={() => setForgotPasswordOpen(true)}
                  sx={{ p: 0, minWidth: 0, fontSize: '12.5px', fontWeight: 600, textTransform: 'none' }}
                >
                  Esqueceu sua senha?
                </Button>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isLoading}
                endIcon={!isLoading && <ArrowRight size={15} />}
                sx={{ height: 50, fontSize: 14, mb: 2.5 }}
              >
                {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Entrar'}
              </Button>

              <Typography sx={{ textAlign: 'center', fontSize: 13, color: 'text.secondary' }}>
                Ainda não tem uma conta?{' '}
                <Box component="button" type="button" onClick={() => toggleMode('cadastro')} sx={{ border: 'none', background: 'none', p: 0, color: 'primary.main', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  Cadastre-se grátis
                </Box>
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={registerFormik.handleSubmit} sx={{ width: '100%' }}>
              {/* Versão compacta (ui-ux/prototipo-criar-conta.html): 3 seções em grid de 2 colunas, campos 46px. */}
              <SectionLabel first>Estabelecimento</SectionLabel>
              <Box sx={compactGridSx}>
                <AuthField
                  sx={[compactFieldSx, { mb: '12px' }]}
                  name="codEnterprise"
                  label="Nome do estabelecimento"
                  autoFocus
                  value={registerFormik.values.codEnterprise}
                  onChange={(e) => registerFormik.setFieldValue('codEnterprise', e.target.value)}
                  error={!!registerFormik.errors.codEnterprise}
                  helperText={registerFormik.errors.codEnterprise}
                />

                <FormControl fullWidth sx={[standaloneFieldSx, compactFieldSx, { mb: '12px', position: 'relative' }]} error={!!registerFormik.errors.segment}>
                  {/* Label micro dentro da caixa (top 6px), como o `.ff-select-label` do protótipo compacto */}
                  <Typography component="label" sx={{ position: 'absolute', left: 14, top: 6, zIndex: 1, pointerEvents: 'none', fontSize: '10px', fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: registerFormik.errors.segment ? 'error.main' : 'primary.main' }}>
                    Segmento
                  </Typography>
                  <Select
                    displayEmpty
                    name="segment"
                    value={registerFormik.values.segment}
                    onChange={(e) => registerFormik.setFieldValue('segment', e.target.value)}
                  >
                    <MenuItem value="" disabled>Selecione o segmento</MenuItem>
                    <MenuItem value="bussiness">Comércio</MenuItem>
                    <MenuItem value="assistance">Assistência ou Prestação de Serviços</MenuItem>
                    <MenuItem value="workshop">Oficina Mecânica</MenuItem>
                  </Select>
                  {registerFormik.errors.segment && (
                    <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>{registerFormik.errors.segment}</Typography>
                  )}
                </FormControl>
              </Box>

              <SectionLabel>Responsável</SectionLabel>
              <Box sx={compactGridSx}>
                <AuthField
                  sx={[compactFieldSx, { mb: '12px' }]}
                  name="name"
                  label="Nome"
                  value={registerFormik.values.name}
                  onChange={(e) => registerFormik.setFieldValue('name', e.target.value)}
                  error={!!registerFormik.errors.name}
                  helperText={registerFormik.errors.name}
                />
                <AuthField
                  sx={[compactFieldSx, { mb: '12px' }]}
                  name="lastName"
                  label="Sobrenome"
                  value={registerFormik.values.lastName}
                  onChange={(e) => registerFormik.setFieldValue('lastName', e.target.value)}
                  error={!!registerFormik.errors.lastName}
                  helperText={registerFormik.errors.lastName}
                />
                <AuthField
                  sx={[compactFieldSx, { mb: '12px' }]}
                  name="email"
                  type="email"
                  label="E-mail"
                  value={registerFormik.values.email}
                  onChange={(e) => registerFormik.setFieldValue('email', e.target.value)}
                  error={!!registerFormik.errors.email}
                  helperText={registerFormik.errors.email}
                />
                <InputMasked
                  name="cellphone"
                  fullWidth
                  label="Celular"
                  variant="outlined"
                  mask="(99) 99999-9999"
                  value={registerFormik.values.cellphone}
                  onChange={(e) => registerFormik.setFieldValue('cellphone', e.target.value)}
                  error={!!registerFormik.errors.cellphone}
                  helperText={registerFormik.errors.cellphone}
                  sx={[standaloneFieldSx, compactFieldSx, { mb: '12px' }]}
                />
              </Box>

              <SectionLabel>Acesso</SectionLabel>
              <Box sx={compactGridSx}>
                <Box>
                  <AuthField
                    sx={[compactFieldSx, { mb: '6px' }]}
                    name="password"
                    label="Senha"
                    type={showRegisterPassword ? 'text' : 'password'}
                    value={registerFormik.values.password}
                    onChange={(e) => registerFormik.setFieldValue('password', e.target.value)}
                    error={!!registerFormik.errors.password}
                    helperText={registerFormik.errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton aria-label="alternar visibilidade da senha" onClick={() => setShowRegisterPassword(!showRegisterPassword)} edge="end">
                            {showRegisterPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <PasswordStrengthMeter
                    password={registerFormik.values.password}
                    emptyLabel="Mínimo de 8 caracteres, com letra maiúscula, número e símbolo"
                    showChecklist={false}
                    sx={{ mb: '12px', '& > div:first-of-type': { height: 5, mt: 0, mb: '3px' }, '& > p': { fontSize: '10.5px', lineHeight: 1.3 } }}
                  />
                </Box>

                <AuthField
                  sx={[compactFieldSx, { mb: '12px' }]}
                  name="confirmPassword"
                  label="Confirmar senha"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={registerFormik.values.confirmPassword}
                  onChange={(e) => registerFormik.setFieldValue('confirmPassword', e.target.value)}
                  error={!!registerFormik.errors.confirmPassword}
                  helperText={confirmPasswordMatches ? undefined : registerFormik.errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {confirmPasswordMatches && <CheckCircle2 size={17} color={theme.palette.success.main} style={{ marginRight: 4 }} />}
                        <IconButton aria-label="alternar visibilidade da senha" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                          {showConfirmPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'stretch', mt: '4px' }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={registerLoading || !termsAccepted}
                  endIcon={!registerLoading && <ArrowRight size={15} />}
                  sx={{ height: 46, fontSize: 14 }}
                >
                  {registerLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Criar minha conta'}
                </Button>
                <FormControlLabel
                  sx={{ m: 0, alignItems: 'center', '& .MuiFormControlLabel-label': { fontSize: 12.5, lineHeight: 1.4, color: 'text.secondary' } }}
                  control={<Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} sx={{ p: '4px', mr: '4px' }} />}
                  label={<>Li e concordo com os {LEGAL_LINKS_ENABLED
                    ? <Box component="a" href="/termos-de-uso" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Termos de Uso</Box>
                    : <Box component="span" aria-disabled="true" tabIndex={-1} onClick={(e) => e.preventDefault()} sx={{ color: 'inherit', fontWeight: 600, cursor: 'default' }}>Termos de Uso</Box>} e a {LEGAL_LINKS_ENABLED
                    ? <Box component="a" href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>Política de Privacidade</Box>
                    : <Box component="span" aria-disabled="true" tabIndex={-1} onClick={(e) => e.preventDefault()} sx={{ color: 'inherit', fontWeight: 600, cursor: 'default' }}>Política de Privacidade</Box>}</>}
                />
              </Box>

              <Typography sx={{ textAlign: 'center', fontSize: 13, color: 'text.secondary', mt: '12px' }}>
                Já tem uma conta?{' '}
                <Box component="button" type="button" onClick={() => toggleMode('login')} sx={{ border: 'none', background: 'none', p: 0, color: 'primary.main', fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                  Entrar
                </Box>
              </Typography>
            </Box>
          )}
        </Box>

        {/* feature pills */}
        <Box
          sx={{
            display: isLoginMode ? 'flex' : { xs: 'none', sm: 'flex' }, gap: isLoginMode ? 1.1 : '8px', flexWrap: 'wrap', justifyContent: 'center',
            mt: isLoginMode ? 3.25 : '14px', maxWidth: isLoginMode ? 460 : 640,
            // Cadastro compacto: chips somem em viewport baixo (< 720px), como no protótipo.
            ...(isLoginMode ? {} : { '@media (max-height:720px)': { display: 'none' } }),
          }}
        >
          {FEATURE_PILLS.map(({ icon: Icon, label }) => (
            <Box
              key={label}
              sx={{
                display: 'flex', alignItems: 'center', gap: '6px', fontSize: 11.5, fontWeight: 600, color: 'text.secondary',
                backgroundColor: alpha(theme.palette.background.paper, 0.55), backdropFilter: 'blur(8px)',
                border: `1px solid ${theme.palette.divider}`, px: '13px', py: '7px', borderRadius: '20px',
              }}
            >
              <Icon size={13} color={theme.palette.primary.main} />
              {label}
            </Box>
          ))}
        </Box>

        <Typography sx={{ mt: isLoginMode ? 2.75 : '10px', fontSize: 11.5, color: 'text.disabled' }}>
          © 2024 FlowVendas
        </Typography>
      </Box>

      {forgotDialog}
    </Box>
  );
}
